import { useMemo } from 'react';
import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import { FaTrash } from 'react-icons/fa';
import { useTable } from 'react-table';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Button } from 'components/button';
import { Copyable } from 'components/copyable';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'components/table';
import { Tooltip } from 'components/tooltip';
import {
  DosageScheduleQuery,
  DosageScheduleQueryVariables,
  RemoveSequenceFromDosageScheduleMutation,
  RemoveSequenceFromDosageScheduleMutationVariables,
} from 'graphql/types';
import { useNotifications } from 'notifications';
import { buildRoute } from 'utils/routes';
import { DosageScheduleMermaid } from './mermaid';

const dosageSchedulePageQuery = gql`
  query DosageSchedule($id: ID!) {
    dosageSchedule: dosageSchedule(id: $id) {
      id
      displayName
      internalName
      maxDaysSinceDoseBeforeIntervention
      minSafeDoseIntervalDays
      sequences {
        id
        internalName
      }
      doses {
        id
        intervalDays
        nextDosageScheduleEventId
        variant {
          id
          name
        }
        dose {
          id
          medicineTradeName
          quantity
          unit
        }
      }
    }
  }
`;

const DosageSchedulePage = () => {
  const { dosageScheduleId } = useParams<{ dosageScheduleId: string }>();
  const history = useHistory();
  const showNotification = useNotifications();

  const { data } = useSuspenseQuery<
    DosageScheduleQuery,
    DosageScheduleQueryVariables
  >(dosageSchedulePageQuery, {
    variables: { id: dosageScheduleId },
  });

  const [removeSequenceFromDosageSchedule] = useMutation<
    RemoveSequenceFromDosageScheduleMutation,
    RemoveSequenceFromDosageScheduleMutationVariables
  >(
    gql`
      mutation RemoveSequenceFromDosageSchedule(
        $input: RemoveSequenceFromDosageScheduleInput!
      ) {
        removeSequenceFromDosageSchedule(input: $input) {
          dosageSchedule {
            id
            sequences {
              id
              internalName
            }
          }
        }
      }
    `,
    {
      refetchQueries: ['DosageSchedule'],
      onCompleted: () => {
        showNotification({
          type: 'success',
          message: 'Sequence removed from dosage schedule',
        });
      },
      onError: () => {
        showNotification({
          type: 'error',
          message: 'Error removing sequence from dosage schedule',
        });
      },
    },
  );

  const columns = useMemo(
    () => [
      {
        id: 'id',
        accessor: 'id' as const,
        Header: 'ID',
        Cell: ({ value }: { value: string }) => (
          <Copyable text={value}>
            {(copied) => (
              <pre className="text-sm cursor-pointer text-slate-500">
                {copied ? 'Copied' : value.slice(-6)}
              </pre>
            )}
          </Copyable>
        ),
      },
      {
        id: 'sequence',
        accessor: 'internalName' as const,
        Header: 'Sequence',
        Cell: ({
          row,
        }: {
          row: { original: { id: string; internalName: string } };
        }) => {
          const sequence = row.original;
          return (
            <Link to={buildRoute.sequence(sequence.id)}>
              {sequence.internalName}
            </Link>
          );
        },
      },
      {
        id: 'delete',
        Header: 'Delete',
        Cell: ({ row }: { row: { original: { id: string } } }) => (
          <Button
            color="danger"
            hoverText="Delete sequence from dosage schedule"
            onClick={() => {
              removeSequenceFromDosageSchedule({
                variables: {
                  input: { dosageScheduleId, sequenceId: row.original.id },
                },
              });
            }}
          >
            <FaTrash size={14} />
          </Button>
        ),
      },
    ],
    [dosageScheduleId, removeSequenceFromDosageSchedule],
  );

  const tableInstance = useTable<{
    __typename?: 'Sequence';
    id: string;
    internalName: string;
  }>({
    columns,
    data: data?.dosageSchedule?.sequences ?? [],
  });

  const dosageSchedule = data.dosageSchedule;
  if (!dosageSchedule) {
    return <div className="font-medium pt-8">Dosage schedule not found</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded shadow bg-white pt-2 border-b border-slate-200">
        <div className="border-b border-slate-200 px-4 py-4">
          <h3 className="text-lg leading-6 font-medium text-slate-900">
            Details
          </h3>
        </div>
        <div className="px-4 py-4">
          <table className="border-separate border-spacing-x-2">
            <tbody>
              <tr>
                <td className="font-medium">ID</td>
                <td>
                  <Copyable text={dosageSchedule.id}>
                    {(copied) => (
                      <pre className="text-sm cursor-pointer">
                        {copied ? 'Copied' : dosageSchedule.id.slice(-6)}
                      </pre>
                    )}
                  </Copyable>
                </td>
              </tr>
              <tr>
                <td className="font-medium">Internal Name</td>
                <td>{dosageSchedule.internalName}</td>
              </tr>
              <tr>
                <td className="font-medium">Display Name</td>
                <td>{dosageSchedule.displayName}</td>
              </tr>
              <tr>
                <td className="font-medium">
                  Minimum Safe Dose Interval (Days)
                  <Tooltip
                    icon="question"
                    hoverText="The smallest safe gap between doses in days."
                  />
                </td>
                <td>{dosageSchedule.minSafeDoseIntervalDays}</td>
              </tr>
              <tr>
                <td className="font-medium">
                  Maximum Days Since Previous Dose Before Intervention
                  <Tooltip
                    icon="question"
                    hoverText="The maximum number of days since the patient's last dose before MS needs to intervene."
                  />
                </td>
                <td>{dosageSchedule.maxDaysSinceDoseBeforeIntervention}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="overflow-hidden rounded shadow bg-white pt-2 border-b border-slate-200">
        <div className="border-b px-4 py-4 mb-5 border-slate-200 flex justify-between items-center">
          <h3 className="text-lg leading-6 font-medium text-slate-900">
            Linked Sequences
            <span className="ml-2 text-slate-500">
              {dosageSchedule.sequences?.length}
            </span>
          </h3>

          <div className="flex justify-between gap-2">
            <Button
              size="small"
              variant="outline"
              onClick={() => {
                history.push(
                  buildRoute.dosageScheduleAddSequence(dosageScheduleId),
                );
              }}
            >
              Add Sequence
            </Button>
          </div>
        </div>
        <Table tableInstance={tableInstance}>
          <TableHead />
          <TableBody>
            {tableInstance.rows?.map((row) => {
              tableInstance.prepareRow(row);
              return (
                <TableRow row={row} key={row.id}>
                  {row.cells.map((cell) => (
                    <TableCell
                      key={`${row.id}-${cell.column.id}`}
                      cell={cell}
                    />
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="rounded shadow bg-white pt-2 border-b border-slate-200">
        <div className="border-b border-slate-200 px-4 py-4">
          <h3 className="text-lg leading-6 font-medium text-slate-900">
            Dosage Schedule
          </h3>
        </div>
        <div className="px-4 py-4">
          {dosageSchedule && (
            <DosageScheduleMermaid dosageSchedule={dosageSchedule} />
          )}
        </div>
      </div>
    </div>
  );
};
export default DosageSchedulePage;
